<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue';
import { CheckCircle2, RefreshCw, X } from 'lucide-vue-next';
import { TRANSLATION_PROVIDER_LABELS, type AdminLicense, type AdminUser } from '@sky-lingo-v2/shared';
import ProviderLogo from './ProviderLogo.vue';
import { providerIds } from './translationProviders';
import { accessDraftPayload, accessLocalDateTime, type AccessDraft, type AccessUpdate } from './userLicenses';

const props = defineProps<{
  user: AdminUser; access: AdminLicense | null; loaded: boolean; loading: boolean; saving: boolean;
  canRead: boolean; canWrite: boolean; error: string; notice: string;
}>();
const emit = defineEmits<{ close: []; refresh: []; save: [payload: AccessUpdate] }>();
const dialog = ref<HTMLDialogElement | null>(null);
const validationError = ref('');
const draft = reactive<AccessDraft>({ providers: [], expiresAt: '', status: '' });
const showForm = computed(() => props.loaded || !props.canRead);
const stateLabel = computed(() => {
  if (!props.access) return '';
  if (props.access.status === 'revoked') return '已停用';
  return props.access.status === 'expired' || Date.parse(props.access.expiresAt) <= Date.now() ? '已到期' : '有效';
});
let previousBodyOverflow = '';
function resetDraft(): void {
  draft.providers = [...(props.access?.providers || [])];
  draft.expiresAt = props.access ? accessLocalDateTime(props.access.expiresAt) : '';
  draft.status = props.access?.status || '';
  validationError.value = '';
}
watch([() => props.access, () => props.loaded, () => props.user.id], resetDraft, { immediate: true });
onMounted(() => { previousBodyOverflow = document.body.style.overflow; document.body.style.overflow = 'hidden'; dialog.value?.showModal(); });
onBeforeUnmount(() => { if (dialog.value?.open) dialog.value.close(); document.body.style.overflow = previousBodyOverflow; });
function close(): void { if (!props.saving) emit('close'); }
function submit(): void {
  if (!props.canWrite || props.saving || props.loading || !showForm.value) return;
  validationError.value = '';
  try {
    const payload = accessDraftPayload(draft, props.loaded ? props.access : null, !props.loaded);
    if (!Object.keys(payload).length) { validationError.value = '没有需要保存的修改'; return; }
    emit('save', payload);
  } catch (error) {
    const code = error instanceof Error ? error.message : '';
    validationError.value = code === 'invalid_provider_policy' ? '请至少选择一种可用模型'
      : code === 'current_access_missing_fields' ? '请明确选择授权状态'
      : '请填写有效的到期时间';
  }
}
function dateTime(value: string): string { return new Date(value).toLocaleString('zh-CN', { hour12: false }); }
</script>

<template>
  <dialog ref="dialog" class="userAccessDialog" aria-labelledby="userAccessTitle" @cancel.prevent="close">
    <header class="userAccessHeader">
      <div><h2 id="userAccessTitle">权限与有效期</h2><p><strong>{{ user.username }}</strong><span class="accessUserId">#{{ user.id }}</span></p></div>
      <button class="iconButton" type="button" aria-label="关闭权限与有效期" :disabled="saving" @click="close"><X :size="18" /></button>
    </header>
    <div class="userAccessBody">
      <div class="userAccessToolbar">
        <div class="userAccessSummary">
          <span v-if="loaded && access">{{ error ? '已加载有效期至' : '当前有效期至' }} <strong>{{ dateTime(access.expiresAt) }}</strong><small>{{ stateLabel }}<span v-if="!canWrite"> · 当前管理员只有查看权限</span></small></span>
          <span v-else-if="!canRead">当前配置不可查看<small>只提交需要修改的项目，留空保持原值。</small></span>
          <span v-else-if="loading">正在读取当前配置…</span>
          <span v-else-if="error">当前配置读取失败，请重试</span>
          <span v-else-if="loaded">尚未设置当前权限</span>
        </div>
        <button v-if="canRead" class="secondaryButton" type="button" :disabled="loading || saving" @click="emit('refresh')"><RefreshCw :size="14" />刷新</button>
      </div>
      <div v-if="error" class="alert error" role="alert">{{ error }}</div>
      <div v-if="notice" class="alert success" role="status">{{ notice }}</div>
      <div v-if="loading && !loaded" class="tableLoading" role="status">正在读取当前权限…</div>
      <form v-if="showForm" class="userAccessForm" @submit.prevent="submit">
        <div class="userAccessFields">
          <label class="compactField"><span>{{ loaded ? '到期时间' : '到期时间（留空保持）' }}</span><input v-model="draft.expiresAt" type="datetime-local" step="1" :required="loaded && !access" :disabled="saving || !canWrite" /></label>
          <label class="compactField"><span>授权状态</span><select v-model="draft.status" :required="loaded && !access" :disabled="saving || !canWrite"><option value="" :disabled="loaded">{{ loaded ? '请选择状态' : '保持不变' }}</option><option value="active">启用</option><option value="expired">已到期</option><option value="revoked">停用</option></select></label>
        </div>
        <fieldset class="accessProviderChoices" :disabled="saving || !canWrite">
          <legend>可用翻译模型 <span>{{ loaded ? '已选' : '本次选择' }} {{ draft.providers.length }} 个{{ loaded ? '' : '，不选保持' }}</span></legend>
          <label v-for="provider in providerIds" :key="provider" :class="{ selected: draft.providers.includes(provider) }"><input v-model="draft.providers" type="checkbox" :value="provider" /><ProviderLogo :provider="provider" /><span>{{ TRANSLATION_PROVIDER_LABELS[provider] }}</span></label>
        </fieldset>
        <p class="accessFormHint">修改模型权限不会自动延长有效期。账号启停在用户管理中设置。</p>
        <div v-if="validationError" class="accessValidation" role="alert">{{ validationError }}</div>
        <div class="userAccessFooter">
          <span v-if="canWrite">保存后覆盖当前配置</span><span v-else>当前管理员只有查看权限</span>
          <button v-if="canWrite" class="primaryButton" type="submit" :disabled="saving || loading"><CheckCircle2 :size="14" />{{ saving ? '正在保存…' : '保存设置' }}</button>
        </div>
      </form>
    </div>
  </dialog>
</template>

<style scoped>
/* 760px sheet = 2 × 20px inset + 720px working area; all form sections share these edges. */
.userAccessDialog { width: min(760px, calc(100vw - 32px)); max-width: none; max-height: calc(100dvh - 32px); margin: auto; padding: 0; border: 1px solid var(--line-strong); border-radius: 12px; color: var(--text); background: var(--surface); box-shadow: 0 20px 60px #0005; }
.userAccessDialog::backdrop { background: #0009; }
.userAccessHeader { position: sticky; top: 0; z-index: 2; display: flex; justify-content: space-between; align-items: flex-start; gap: 16px; padding: 18px 20px 14px; border-bottom: 1px solid var(--line); background: var(--surface); }
.userAccessHeader h2 { margin: 0; font-size: 20px; font-weight: 600; }
.userAccessHeader p { margin: 6px 0 0; font-size: 13px; }
.accessUserId { margin-left: 10px; color: var(--muted); font-variant-numeric: tabular-nums; }
.userAccessHeader .iconButton { display: grid; width: 32px; height: 32px; place-items: center; border: 1px solid var(--line-strong); border-radius: 6px; background: transparent; cursor: pointer; }
.userAccessBody { padding: 16px 20px 18px; }
.userAccessToolbar { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; margin-bottom: 16px; }
.userAccessSummary { min-width: 0; font-size: 13px; }
.userAccessSummary strong { font-weight: 500; font-variant-numeric: tabular-nums; }
.userAccessSummary small { display: block; margin-top: 6px; color: var(--muted); font-size: 12px; line-height: 1.6; }
.userAccessToolbar button { min-height: 32px; padding: 6px 10px; border-radius: 6px; font-size: 12px; }
.userAccessForm { margin: 0; padding: 0; }
.userAccessFields { display: grid; grid-template-columns: minmax(200px, 1fr) minmax(140px, 200px); gap: 16px; margin-bottom: 18px; }
.userAccessFields input, .userAccessFields select { width: 100%; min-height: 36px; box-sizing: border-box; }
.userAccessFields input:disabled, .userAccessFields select:disabled { color: var(--text); opacity: 1; }
.accessProviderChoices { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 6px 10px; margin: 0; padding: 0; border: 0; }
.accessProviderChoices legend { margin-bottom: 8px; padding: 0; font-size: 12px; color: var(--muted); }
.accessProviderChoices legend span { margin-left: 10px; }
.accessProviderChoices label { display: flex; align-items: center; gap: 7px; min-width: 0; min-height: 36px; padding: 6px; border: 1px solid transparent; border-radius: 6px; font-size: 12px; cursor: pointer; }
.accessProviderChoices label.selected { border-color: var(--line-strong); background: var(--surface-soft); }
.accessProviderChoices label > span { overflow-wrap: anywhere; }
.accessProviderChoices input { flex: 0 0 auto; margin: 0; }
.accessProviderChoices :deep(.provider-logo) { width: 18px; height: 18px; flex-shrink: 0; }
.accessFormHint { margin: 14px 0 0; color: var(--muted); font-size: 12px; line-height: 1.6; }
.accessValidation { margin-top: 10px; color: var(--danger); font-size: 12px; }
.userAccessFooter { display: flex; justify-content: space-between; align-items: center; gap: 12px; margin-top: 18px; padding-top: 14px; border-top: 1px solid var(--line); }
.userAccessFooter > span { color: var(--muted); font-size: 12px; }
.userAccessFooter .primaryButton { min-height: 36px; padding: 7px 12px; border-radius: 6px; background: var(--text); color: var(--surface); box-shadow: none; font-size: 12px; }
.userAccessDialog button:disabled { cursor: not-allowed; }
.userAccessDialog button:focus-visible, .accessProviderChoices label:focus-within { outline: 2px solid var(--accent); outline-offset: 2px; }
@media (max-width: 640px) {
  .userAccessDialog { width: calc(100vw - 16px); max-height: calc(100dvh - 16px); border-radius: 8px; }
  .userAccessHeader { padding: 16px 14px 12px; }
  .userAccessHeader h2 { font-size: 18px; }
  .userAccessBody { padding: 14px; }
  .userAccessHeader .iconButton, .userAccessToolbar button { min-height: 44px; min-width: 44px; }
  .userAccessFields { grid-template-columns: 1fr; gap: 12px; }
  .accessProviderChoices { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 6px; }
  .accessProviderChoices label { min-height: 44px; }
  .userAccessFooter { align-items: flex-end; }
  .userAccessFooter .primaryButton { min-height: 44px; }
}
</style>
